import { useEffect, useRef } from 'react';

/**
 * Keyboard shortcut system.
 *
 * Keys are written as `mod+k` (mod is Cmd on macOS, Ctrl elsewhere), `shift+/`, single keys such
 * as `c`, or two-key sequences such as `g h`. Shortcuts without a modifier are ignored while the
 * user is typing in an input, textarea or editor.
 */
export interface Shortcut {
  keys: string;
  description: string;
  group: string;
  handler: (e: KeyboardEvent) => void;
  /** Fire even when focus is in a text field (only sensible for modifier combos and Escape). */
  allowInInput?: boolean;
}

const registry = new Set<Shortcut>();
const listeners = new Set<() => void>();
let pendingPrefix: string | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function normalize(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  if (e.altKey) parts.push('alt');
  let key = e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
  if (key === ' ') key = 'space';
  // `?` already implies shift; only add shift for letters and named keys.
  if (e.shiftKey && (key.length > 1 || /[a-z0-9]/.test(key))) parts.push('shift');
  parts.push(key);
  return parts.join('+');
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.defaultPrevented || e.isComposing) return;
  if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return;
  const combo = normalize(e);
  const typing = isTyping(e.target);
  const candidates = pendingPrefix ? [`${pendingPrefix} ${combo}`, combo] : [combo];
  const hadPrefix = pendingPrefix !== null;
  clearPending();

  for (const candidate of candidates) {
    for (const s of registry) {
      if (s.keys !== candidate) continue;
      if (typing && !s.allowInInput && !candidate.includes('mod+')) continue;
      e.preventDefault();
      s.handler(e);
      return;
    }
  }
  if (typing || hadPrefix || combo.includes('+')) return;
  // Start a sequence if any shortcut begins with this key.
  for (const s of registry) {
    if (s.keys.startsWith(`${combo} `)) {
      pendingPrefix = combo;
      pendingTimer = setTimeout(clearPending, 1200);
      return;
    }
  }
}

function clearPending(): void {
  pendingPrefix = null;
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
}

let attached = false;
function attach(): void {
  if (attached) return;
  attached = true;
  window.addEventListener('keydown', onKeyDown);
}

export function registerShortcut(shortcut: Shortcut): () => void {
  attach();
  registry.add(shortcut);
  listeners.forEach((l) => l());
  return () => {
    registry.delete(shortcut);
    listeners.forEach((l) => l());
  };
}

/** Registers a shortcut for the lifetime of the component. The handler can change between renders. */
export function useShortcut(
  keys: string,
  handler: (e: KeyboardEvent) => void,
  opts: { description: string; group?: string; allowInInput?: boolean; enabled?: boolean },
): void {
  const ref = useRef(handler);
  ref.current = handler;
  const { description, group = 'General', allowInInput, enabled = true } = opts;
  useEffect(() => {
    if (!enabled) return;
    return registerShortcut({ keys, description, group, allowInInput, handler: (e) => ref.current(e) });
  }, [keys, description, group, allowInInput, enabled]);
}

export function listShortcuts(): Shortcut[] {
  return [...registry];
}

export function onShortcutsChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Display form: `mod+k` becomes ["⌘", "K"] on macOS and ["Ctrl", "K"] elsewhere. */
export function formatKeys(keys: string): string[][] {
  return keys.split(' ').map((combo) =>
    // A lone "+" is a key, not a separator.
    (combo === '+' ? ['+'] : combo.split('+').filter(Boolean)).map((k) => {
      if (k === 'mod') return isMac ? '⌘' : 'Ctrl';
      if (k === 'shift') return isMac ? '⇧' : 'Shift';
      if (k === 'alt') return isMac ? '⌥' : 'Alt';
      if (k === 'enter') return '↵';
      if (k === 'escape') return 'Esc';
      if (k === 'arrowup') return '↑';
      if (k === 'arrowdown') return '↓';
      return k.length === 1 ? k.toUpperCase() : k[0]!.toUpperCase() + k.slice(1);
    }),
  );
}
